import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowRight,
  Leaf,
  Drumstick
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayColors = {
  Monday: 'from-black/40 to-black/20',
  Tuesday: 'from-black/40 to-black/20',
  Wednesday: 'from-black/40 to-black/20',
  Thursday: 'from-black/40 to-black/20',
  Friday: 'from-black/40 to-black/20',
  Saturday: 'from-black/40 to-black/20',
  Sunday: 'from-black/40 to-black/20',
};

const dayImages = {
  Monday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/96r60qzo_dal%20geera%20chicken%20fry.png',
  Tuesday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/mofh1cvo_image.png',
  Wednesday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/1geks9ry_bagara%20with%20chicken%20curry.png',
  Thursday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/ukajjtqp_butter%20chicken%20geera%20rice.jpeg',
  Friday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/wr9pienz_bagara%20rice%20and%20kamb%20curry.jpeg',
  Saturday: 'https://images.pexels.com/photos/17696653/pexels-photo-17696653.jpeg?auto=compress&cs=tinysrgb&w=600',
  Sunday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/pfhdhwts_image.png',
};

const vegDayImages = {
  Monday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/2b4kau9j_image.png',
  Tuesday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/t6odl5q3_image.png',
  Wednesday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/xscewcap_image.png',
  Thursday: 'https://static.prod-images.emergentagent.com/jobs/8dcb2a80-f509-441b-b99e-0f9224a5cf02/images/32e646ad046d764d3e0272ffeb4f1007c2e968a0a973d2587a4950a96132b95a.png',
  Friday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/qu3z7q2d_image.png',
  Saturday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/2x7f0p0j_image.png',
  Sunday: 'https://customer-assets.emergentagent.com/job_anan-meal-prep/artifacts/vtyo83od_image.png',
};

export default function Menu() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mealType, setMealType] = useState('non-veg');

  const fetchMenu = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenu(response.data);
    } catch (error) {
      // Menu fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]" data-testid="menu-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E05D3A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0]" data-testid="menu-page">
      {/* Header */}
      <section className="bg-[#2C402E] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4">7-Day Set Menu</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-4" data-testid="menu-title">
            Weekly Menu
          </h1>
          <p className="text-[#FAF6F0]/80 text-lg max-w-2xl mx-auto">
            Fresh, healthy meals every day of the week. Choose between vegetarian and non-vegetarian options.
          </p>
        </div>
      </section>

      {/* Meal Type Toggle */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Tabs value={mealType} onValueChange={setMealType} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-white shadow-lg rounded-full p-1">
            <TabsTrigger 
              value="veg" 
              className="flex items-center gap-2 data-[state=active]:bg-[#2C402E] data-[state=active]:text-white rounded-full"
              data-testid="veg-tab"
            >
              <Leaf className="w-4 h-4" />
              Vegetarian
            </TabsTrigger>
            <TabsTrigger 
              value="non-veg" 
              className="flex items-center gap-2 data-[state=active]:bg-[#E05D3A] data-[state=active]:text-white rounded-full"
              data-testid="non-veg-tab"
            >
              <Drumstick className="w-4 h-4" />
              Non-Vegetarian
            </TabsTrigger>
          </TabsList>

          <TabsContent value="veg" className="mt-8">
            <MenuGrid menu={menu?.weekly_menu} type="veg" />
          </TabsContent>
          <TabsContent value="non-veg" className="mt-8">
            <MenuGrid menu={menu?.weekly_menu} type="non-veg" />
          </TabsContent>
        </Tabs>
      </div>

      {/* Pricing CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-[#E05D3A] text-white p-8 md:p-12 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl font-bold mb-2">
                  Ready to Eat Healthy?
                </h2>
                <p className="text-white/80">
                  Subscribe today and enjoy all 7 days of delicious meals with free delivery!
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black">£60<span className="text-lg font-normal">/wk</span></div>
                  <div className="text-white/70 text-sm">or £229/mo</div>
                </div>
                <Button 
                  size="lg"
                  className="bg-white text-[#E05D3A] hover:bg-[#FAF6F0] font-bold rounded-full"
                  onClick={() => navigate('/subscribe')}
                  data-testid="menu-subscribe-btn"
                >
                  Subscribe Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function MenuGrid({ menu, type }) {
  if (!menu) return null;

  const images = type === 'veg' ? vegDayImages : dayImages;

  return (
    <div className="bento-grid" data-testid="menu-grid">
      {menu.map((day, index) => (
        <Card 
          key={day.day}
          className="menu-card bg-white border border-[#2C402E]/10 overflow-hidden rounded-2xl"
          data-testid={`menu-card-${day.day.toLowerCase()}`}
        >
          <div className={`h-40 md:h-48 bg-gradient-to-br ${dayColors[day.day]} relative overflow-hidden`}>
            <img 
              src={images[day.day]}
              alt={day.day}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 20%' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="font-serif text-2xl font-bold drop-shadow-lg">{day.day}</h3>
              </div>
            </div>
          </div>
          <div className="p-6">
            <h4 className="font-serif font-semibold text-lg text-[#2C402E]">
              {type === 'veg' ? day.veg_option : day.non_veg_option}
            </h4>
            {day.extras && <p className="text-sm text-[#4A4A4A] mt-2">{day.extras}</p>}
          </div>
        </Card>
      ))}
    </div>
  );
}
